/**
 * Facebook came back without an email address.
 *
 * `docs/SOCIAL_LOGIN.md`: "it may return no email at all — the user registered by phone, or
 * declined the permission. Handle it as first-class, not as an error: ask for an email, verify
 * it by mail, and only then create or link. Never fabricate a placeholder address."
 *
 * So this is a screen with a form, not a failure with a retry button. Nothing exists yet — the
 * identity is parked on the single-use state row the callback issued, and the account is created
 * only when the link in the verification mail is opened.
 */

import { useState } from 'react'
import type { FormEvent, ReactElement } from 'react'
import { Link } from 'react-router-dom'

import { errorMessage, isApiError } from '../../../lib/api'
import { useT, useTNode } from '../../../lib/i18n'
import { Button } from '../../../components/ui/Button'
import { Field } from '../../../components/ui/Field'
import { Input } from '../../../components/ui/Input'
import { Icon } from '../../../components/ui/Icon'
import { InlineError } from '../../../components/ui/ErrorState'
import { AuthLayout } from '../AuthLayout'
import { emailProblem } from '../validation'
import { submitOAuthEmail } from './api'
import { missingEmailCopy } from './copy'
import type { SocialCopyContext } from './copy'
import { ProviderButton } from './ProviderButton'
import { SOCIAL_LABEL, normaliseSocialErrorCode, socialStartUrl } from './contract'
import type { SocialProvider } from './contract'

export interface MissingEmailScreenProps {
  ctx: SocialCopyContext
  provider: SocialProvider
  /** The single-use row the identity is parked on. Absent means the flow has to restart. */
  state: string | null
  returnTo: string
}

type Stage = 'asking' | 'sent'

export const MissingEmailScreen = ({
  ctx,
  provider,
  state,
  returnTo,
}: MissingEmailScreenProps): ReactElement => {
  const t = useT()
  const tn = useTNode()
  const copy = missingEmailCopy(t, { ...ctx, provider })
  const label = SOCIAL_LABEL[provider]

  const [email, setEmail] = useState('')
  const [fieldError, setFieldError] = useState<string | undefined>(undefined)
  const [formError, setFormError] = useState<string | null>(null)
  const [expired, setExpired] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [stage, setStage] = useState<Stage>('asking')
  const [sentTo, setSentTo] = useState('')

  const restart = (): void => {
    window.location.assign(socialStartUrl(provider, returnTo))
  }

  /* The state row is single-use and lives ten minutes. Without it there is nothing to attach
     an address to, so the only honest offer is to start the consent again. */
  if (state === null || expired) {
    return (
      <AuthLayout
        title={t('auth.social.restartTitle')}
        subtitle={t('auth.social.restartBody', { provider: label })}
        footer={<BackToSignIn />}
      >
        <div className="flex flex-col gap-3">
          <ProviderButton provider={provider} onSelect={restart} />
        </div>
      </AuthLayout>
    )
  }

  if (stage === 'sent') {
    return (
      <AuthLayout
        title={t('auth.checkInbox.title')}
        subtitle={tn('auth.social.sentSubtitle', {
          email: sentTo,
          address: (chunk) => <span className="font-semibold text-ink">{chunk}</span>,
        })}
        footer={<BackToSignIn />}
      >
        <div className="flex items-start gap-3 rounded-md border border-hair bg-surface p-4">
          <Icon name="info" size={18} className="mt-0.5 shrink-0 text-info" />
          <p className="t-small text-ink-mute">
            {tn('auth.social.tryAnotherAddress', {
              again: (chunk) => (
                <button
                  type="button"
                  onClick={() => {
                    setStage('asking')
                  }}
                  className="ring-focus font-semibold text-brass-deep underline underline-offset-2"
                >
                  {chunk}
                </button>
              ),
            })}
          </p>
        </div>
      </AuthLayout>
    )
  }

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    const fault = emailProblem(email)
    setFieldError(fault === null ? undefined : t(fault))
    setFormError(null)
    if (fault !== null) return

    const address = email.trim()
    setSubmitting(true)
    try {
      const result = await submitOAuthEmail(provider, state, address)
      setSentTo(result.email)
      setStage('sent')
    } catch (error) {
      const code = isApiError(error) ? normaliseSocialErrorCode(error.code) : ''
      if (code === 'OAUTH_STATE_INVALID') {
        setExpired(true)
      } else if (code === 'OAUTH_EMAIL_IN_USE') {
        setFieldError(
          t('auth.social.addressInUse', { productName: ctx.productName, provider: label }),
        )
      } else {
        setFormError(errorMessage(error))
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthLayout title={copy.title} subtitle={copy.body} footer={<BackToSignIn />}>
      <form
        noValidate
        onSubmit={(event) => {
          void onSubmit(event)
        }}
        className="flex flex-col gap-4"
      >
        {formError ? <InlineError>{formError}</InlineError> : null}

        <Field
          label={t('auth.fields.email')}
          hint={t('auth.social.emailHint')}
          error={fieldError}
          required
        >
          <Input
            type="email"
            name="email"
            value={email}
            autoComplete="email"
            autoFocus
            placeholder={t('auth.fields.emailPlaceholder')}
            onChange={(event) => {
              setEmail(event.target.value)
            }}
          />
        </Field>

        <Button type="submit" variant="primary" size="lg" fullWidth loading={submitting}>
          {t('auth.social.sendVerification')}
        </Button>

        <p className="t-small text-ink-mute">{copy.hint}</p>
      </form>
    </AuthLayout>
  )
}

const BackToSignIn = (): ReactElement => {
  const t = useT()
  return (
    <p className="t-small text-ink-mute">
      <Link to="/login" className="ring-focus font-semibold text-brass-deep underline underline-offset-2">
        {t('auth.common.backToSignIn')}
      </Link>
    </p>
  )
}
